import _ from 'lodash';
import { RequestDefaultSearchState } from '@/@dtos/search-result.dto';
import transferEfqWithChild from '@/utils/transfer-efq-with-child';
import { Ranking } from '@patsnap-biz/refine-filter/src/Ranking';
import { FIELD_TYPES as fieldTypeMap } from 'library-@/left-refine-filter/config/fieldType.js';
import { getIsSpecialIp } from '@/utils/getIsSpecialIp';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { FetchSrpFilterInit, SrpFilterSort } from '@/services/srp-left-filter-service';

const TOOLTIP_FIELDS = {
    SIMPLE_LEGAL_STATUS: 'snapBusiness.filter.SIMPLE_LEGAL_STATUS',
    EFAM_COUNTRY: 'snapBusiness.filter.EFAM_COUNTRY',
    FAM_COUNTRY: 'snapBusiness.filter.FAM_COUNTRY',
    SEP_SOURCE: 'snapBusiness.filter.SEP_SOURCE',
    PATENT_TYPE: 'snapBusiness.filter.PATENT_TYPE',
    ANCS: 'snapBusiness.filter.ANCS',
    CPC_ALL_FACET: 'snapBusiness.filter.CPC_ALL_FACET',
    TECH_DOMAIN: 'snapBusiness.filter.TECH_DOMAIN',
    EPDS: 'snapBusiness.filter.EPDS'
};

const { forEach, pick, includes, indexOf } = _;

const COMBINATION_FIELDS: { [key: string]: Ranking.FieldKey[] } = {
    Classification: ['IPC_FACET', 'CPC_FACET', 'LOC_FACET', 'UPC_FACET']
};

export const COMBINATION_FIELDS_HASH = Object.keys(COMBINATION_FIELDS).reduce((ret, type) => {
    COMBINATION_FIELDS[type].forEach(k => ret[k] = type);
    return ret;
}, {});

function getFilterViewTypeStructure(filtersItems: any[], isRoot: boolean = true, displayedFilters: any[] = []): SearchResult.FilterViewItem[] {
    const ret: SearchResult.FilterViewItem[] = [];
    const existedFields = {};
    for (let i = 0; i < filtersItems.length; i += 1) {
        let { field, isExpand = false, selected, groups = [] } = filtersItems[i];
        const alias = COMBINATION_FIELDS_HASH[field];
        const item: SearchResult.FilterViewItem = {
            field,
            type: groups.length ? 'group' : 'default',
            tooltip: TOOLTIP_FIELDS[field],
            ...pick(filtersItems[i], ['name']),
        };
        // 如果用户设置过，则将用户设置的前两项默认打开，其他选项关闭
        if (isRoot && displayedFilters && displayedFilters.length > 0) {
            isExpand = false;
            let fieldIndex = indexOf(displayedFilters, field);
            // eslint-disable-next-line eqeqeq
            if (fieldIndex != -1 && fieldIndex <= 1) {
                isExpand = true;
            }
            // 四合一四合一强制打开IPC 小类
            let combinationIndex = indexOf(displayedFilters, 'Classification');
            // eslint-disable-next-line eqeqeq
            if (combinationIndex != -1 && combinationIndex <= 1 && field === 'IPC_FACET') {
                isExpand = true;
            }
        }

        if (item.type === 'default') {
            item.selected = isExpand || selected;
            Object.assign(item, pick(filtersItems[i], ['showExplain', 'collapseNum']));
        }

        if (groups.length > 0) {
            item.children = getFilterViewTypeStructure(groups, false);
            // 后端返回不展开的字段，默认都删除 selected 参数
            // 目前需求中，只需要处理 ANCS 的默认选中，Node 会进行保存
            item.defaultChild = (item.children.find(({ selected }) => !!selected) || { field: undefined }).field;
            item.children.forEach(item => Object.assign(item, {
                ...pick(filtersItems[i], ['showExplain', 'collapseNum']),
                selected: isExpand ? item.selected : false
            }));
        }

        // 只有根节点的 filter 才可以是 combination，目前不允许嵌套类型
        if (!(alias && isRoot)) {
            ret.push(item);
            continue;
        }

        // 四合一类特殊 filters 合并成同一个功能
        let combineField: SearchResult.FilterViewItem = {
            type: 'combination',
            field: alias,
            selected: item.selected
        };

        if (existedFields[alias]) {
            // 后面进入的 combination 类型的所有 field 都要设为 selected: false
            if (item.children) {
                item.children = item.children.map(item => ({ ...item, selected: false }));
            } else {
                item.selected = false;
            }
            // 直接修改之前生成的 combination 类型的 children
            existedFields[alias].children.push(item);
        } else {
            // 使用新的 combination 结构
            combineField.children = [item];
            existedFields[alias] = combineField;
            ret.push(combineField);
        }
    }
    return ret;
}

export async function fetchFilterInit(params: RequestDefaultSearchState) {
    const service1 = injectService<FetchSrpFilterInit>(FetchSrpFilterInit);

    const data = await service1.fetchSrpFilterInit({
        ...params,
        ...window.THIRD_PARTY_PARAMS,
    });

    const service = injectService<SrpFilterSort>(SrpFilterSort)
    let displayedFilters = await service.fetchSrpFilterSort();

    let filterViewStructure = getFilterViewTypeStructure((data.filterConfigs || []), true, displayedFilters);
    let unDisplayedFilters: string[] = [];

    const isSpecialIp = await getIsSpecialIp()
    if (isSpecialIp || displayedFilters.length === 0) {
        displayedFilters = filterViewStructure.map(item => item.field);
    }

    const filterStructureByFieldKey = {};
    forEach(filterViewStructure, item => {
        filterStructureByFieldKey[item.field] = item;
        if (!includes(displayedFilters, item.field)) {
            unDisplayedFilters.push(item.field);
        }
    });

    const result = {
        suggestionFields: data.suggestionFields || ['GNAME', 'ANCS'], // TODO: 需要 Node.JS 额外返回
        externalFieldsQueries: (data.efq || []).map((item, id) => ({
            id,
            ...item,
            ...transferEfqWithChild(item)
        })),
        filterStructureByFieldKey,
        fieldTypeMap,
        fallbackI18n: data.otherConfigs || [],
        displayedFilters,
        unDisplayedFilters
    };
    return result;
}

/**
 * @deprecated
 */
export default fetchFilterInit;
